import { IonBadge, IonInput } from "@ionic/react";
import Fuse from "fuse.js";
import React from "react";

import "./RuleSelector.scss";

interface Rule {
  name: string;
  key: string;
  selected: boolean;
}

interface SearchResult {
  item: Rule;
  refIndex: number;
}

export default class RulesSelector extends React.Component<any, any> {
  protected el: React.RefObject<HTMLDivElement>;
  onToggle: (rule: CustomEvent) => void;

  constructor(props: Readonly<any> | any) {
    super(props);
    this.el = React.createRef();
    this.onToggle = this.props.onToggle;
    this.state = {
      fuse: null,
      searchResults: [],
      rules: [
        { key: "accepted", name: "Accepted" },
        { key: "active_url", name: "Active URL" },
        { key: "after", name: "After (Date)" },
        { key: "after_or_equal", name: "After Or Equal (Date)" },
        { key: "alpha", name: "Alpha" },
        { key: "alpha_dash", name: "Alpha Dash" },
        { key: "alpha_numeric", name: "Alpha Numeric" },
        { key: "array", name: "Array" },
        { key: "bail", name: "Bail" },
        { key: "before", name: "Before (Date)" },
        { key: "before_or_equal", name: "Before Or Equal (Date)" },
        { key: "between", name: "Between" },
        { key: "boolean", name: "Boolean" },
        { key: "confirmed", name: "Confirmed" },
        { key: "date", name: "Date" },
        { key: "date_equals", name: "Date Equals" },
        { key: "date_format", name: "Date Format" },
        { key: "different", name: "Different" },
        { key: "digits", name: "Digits" },
        { key: "digits_between", name: "Digits Between" },
        { key: "dimensions", name: "Dimensions (Image Files)" },
        { key: "distinct", name: "Distinct" },
        { key: "email", name: "Email" },
        { key: "ends_with", name: "Ends With" },
        { key: "exclude_if", name: "Exclude If" },
        { key: "exclude_unless", name: "Exclude Unless" },
        { key: "exists", name: "Exists (Database)" },
        { key: "file", name: "File" },
        { key: "filled", name: "Filled" },
        { key: "greater_than", name: "Greater Than" },
        { key: "greater_than_or_equal", name: "Greater Than Or Equal" },
        { key: "image", name: "Image (File)" },
        { key: "in", name: "In" },
        { key: "in_array", name: "In Array" },
        { key: "integer", name: "Integer" },
        { key: "ip_address", name: "IP Address" },
        { key: "json", name: "JSON" },
        { key: "less_than", name: "Less Than" },
        { key: "less_than_or_equal", name: "Less Than Or Equal" },
        { key: "max", name: "Max" },
        { key: "mime_types", name: "MIME Types" },
        { key: "mime_type_by_file_extension", name: "MIME Type By File Extension" },
        { key: "min", name: "Min" },
        { key: "multiple_of", name: "Multiple Of" },
        { key: "not_in", name: "Not In" },
        { key: "not_regex", name: "Not Regex" },
        { key: "nullable", name: "Nullable" },
        { key: "numeric", name: "Numeric" },
        { key: "password", name: "Password" },
        { key: "present", name: "Present" },
        { key: "regular_expression", name: "Regular Expression" },
        { key: "required", name: "Required" },
        { key: "required_if", name: "Required If" },
        { key: "required_unless", name: "Required Unless" },
        { key: "required_with", name: "Required With" },
        { key: "required_with_all", name: "Required With All" },
        { key: "required_without", name: "Required Without" },
        { key: "required_without_all", name: "Required Without All" },
        { key: "same", name: "Same" },
        { key: "size", name: "Size" },
        { key: "sometimes", name: "Sometimes" },
        { key: "starts_with", name: "Starts With" },
        { key: "string", name: "String" },
        { key: "timezone", name: "Timezone" },
        { key: "unique", name: "Unique (Database)" },
        { key: "url", name: "URL" },
        { key: "uuid", name: "UUID" },
        { key: "swift", name: "Swift" },
      ],
    };
  }

  render() {
    // @ts-ignore
    return (
      <div ref={this.el} className="rules-selector" onKeyDown={(e) => this.handleClear(e)}>
        <IonInput type="text" placeholder="E.g: required" className="rules-selector__input" onIonChange={(e) => this.handleChange(e)} />
        <div className="rules-selector__options">
          {this.state.searchResults.map((result: SearchResult) => (
            <IonBadge
              tabIndex={0}
              key={result.refIndex}
              color={result.item.selected ? "primary" : "secondary"}
              onClick={() => this.handleSelect(result)}
              onKeyDown={(e) => e.key === "Enter" && this.handleSelect(result)}
            >
              {result.item.name}
            </IonBadge>
          ))}
        </div>
      </div>
    );
  }

  componentDidMount() {
    const rules = this.state.rules.map((rule: Rule) => {
      rule.selected = this.props.selected.includes(rule.key);
      return rule;
    });

    const fuse = new Fuse(rules, {
      keys: [
        { name: "name", weight: 0.25 },
        { name: "key", weight: 0.75 },
      ],
    });

    this.setState({ rules, fuse });

    this.handleResults([]);
  }

  handleChange(e: CustomEvent) {
    this.handleResults(this.state.fuse.search(e.detail.value).splice(0, 10));
  }

  handleResults(searchResults: SearchResult[]) {
    if (!searchResults.length) {
      searchResults = this.state.rules
        .filter((rule: Rule | any, key: number) => {
          rule.index = key;
          return rule.selected;
        })
        .map((rule: Rule | any) => ({ item: rule, refIndex: rule.index }));
    }

    this.setState({ searchResults });
  }

  handleSelect(selected: SearchResult) {
    const { rules } = this.state;

    rules[selected.refIndex].selected = !rules[selected.refIndex].selected;

    const el: HTMLDivElement | any = this.el.current;
    const event = new CustomEvent("onChange", {
      detail: {
        value: this.state.rules.filter((rule: Rule) => rule.selected).map((rule: Rule): string => rule.key),
      },
    });

    if (el) {
      el.name = this.props.name;
      el.dispatchEvent(event);
    }

    this.onToggle(event);

    this.setState({ rules });
  }

  handleClear(e: any) {
    if (e.key === "Escape") {
      this.handleResults([]);
      e.target.value = "";
    }
  }
}
